import React, { useEffect, useState } from 'react';
import { HealthCard } from './HealthCard';
import { IntegrityCheckSlideout } from './IntegrityCheckSlideout';
import { useIntegrityChecks } from '@/hooks/useIntegrityChecks';
import { useSourceStaleness } from '@/hooks/useSourceStaleness';
import {
  Globe,
  AlertTriangle,
  FileSearch,
  Activity,
  Shield,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface WorkerStatus {
  status: 'online' | 'offline';
  minutesAgo: number | null;
  jobsActive: number;
}

interface HealthStripProps {
  sourceCount: number;
  stuckTotal: number;
  needsReviewTotal: number;
  workerStatus: WorkerStatus;
  loading: boolean;
}

export const HealthStrip: React.FC<HealthStripProps> = ({
  sourceCount,
  stuckTotal,
  needsReviewTotal,
  workerStatus,
  loading,
}) => {
  const {
    latest: integrityLatest,
    fetchLatest,
    trigger: triggerCheck,
    loading: checkLoading,
  } = useIntegrityChecks();
  const { staleCount, refresh: refreshStaleness } = useSourceStaleness();
  const [slideoutOpen, setSlideoutOpen] = useState(false);

  useEffect(() => {
    fetchLatest();
  }, [fetchLatest]);
  useEffect(() => {
    refreshStaleness();
  }, [refreshStaleness]);

  const handleRunCheck = async () => {
    setSlideoutOpen(true);
    try {
      await triggerCheck();
    } catch {
      /* ignore */
    }
  };

  const integrityResult = integrityLatest?.result;
  const integrityPassed = integrityResult
    ? `${integrityResult.passed}/${integrityResult.total}`
    : '-';
  const integrityStatus = integrityLatest?.status;
  const integrityTime = integrityLatest?.finished_at
    ? new Date(integrityLatest.finished_at).toLocaleString()
    : integrityLatest?.started_at
      ? new Date(integrityLatest.started_at).toLocaleString()
      : null;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <HealthCard
          icon={Globe}
          label="Sources"
          value={sourceCount}
          subtext={loading ? undefined : `${staleCount} flagged stale`}
          subtextColor={staleCount > 0 ? 'text-amber-600' : 'text-gray-500'}
          href="/sources/jobs"
          loading={loading}
        />

        <HealthCard
          icon={AlertTriangle}
          label="Stuck artifacts"
          value={stuckTotal}
          subtext="across all pipeline stages"
          subtextColor={stuckTotal > 0 ? 'text-red-600' : 'text-gray-500'}
          href="/sources/reconcile"
          loading={loading}
        />

        <HealthCard
          icon={FileSearch}
          label="Needs review"
          value={needsReviewTotal}
          subtext={loading ? undefined : 'attendees / motions / financials'}
          subtextColor={
            needsReviewTotal > 0 ? 'text-amber-600' : 'text-gray-500'
          }
          href="/sources/reconcile"
          loading={loading}
        />

        <HealthCard
          icon={Activity}
          label="Worker"
          value={workerStatus.status === 'online' ? 'Running' : 'Offline'}
          subtext={
            loading
              ? undefined
              : workerStatus.status === 'online'
                ? `${workerStatus.jobsActive} active jobs`
                : workerStatus.minutesAgo !== null
                  ? `Last heartbeat ${workerStatus.minutesAgo}m ago`
                  : 'No heartbeat data'
          }
          subtextColor={
            workerStatus.status === 'online' ? 'text-green-600' : 'text-red-600'
          }
          href="/sources/health"
          loading={loading}
        />

        <HealthCard
          icon={Shield}
          label="Data integrity"
          value={`${integrityPassed} passed`}
          href={undefined}
          loading={loading && !integrityLatest}
        >
          <div className="mt-2 space-y-1">
            {integrityTime && (
              <p className="text-xs text-gray-400">{integrityTime}</p>
            )}
            {integrityStatus === 'running' && (
              <p className="text-xs text-blue-600 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Running...
              </p>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={checkLoading}
              onClick={(e) => {
                e.preventDefault();
                handleRunCheck();
              }}
              className="w-full text-xs"
            >
              {checkLoading ? (
                <Loader2 className="w-3 h-3 animate-spin mr-1" />
              ) : (
                <RefreshCw className="w-3 h-3 mr-1" />
              )}
              {checkLoading ? 'Checking...' : 'Run check'}
            </Button>
          </div>
        </HealthCard>
      </div>

      <IntegrityCheckSlideout
        open={slideoutOpen}
        onClose={() => setSlideoutOpen(false)}
        latest={integrityLatest}
        running={checkLoading}
      />
    </>
  );
};
